import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { WorkQueueCase, OutgoingAction } from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { practiceDoctorLabel } from './util';
import { initials } from './caseInitials';
import { TeamPill } from '@odo/shared-ui/components/TeamPill';
import { PrioritySelector } from './priority/PrioritySelector';
import { CompletionCardAssignees } from './CompletionCardAssignees';
import { NextCaseButton, type NextCaseLink } from './NextCaseButton';

interface CompletionCardProps {
    completedCase: WorkQueueCase;
    completedAction: OutgoingAction;
    /** Fresh case fetched after advance — carries the accurate new status, phase, team, priority, per-team assignees. */
    nextStatus?: Pick<
        WorkQueueCase,
        'statusId' | 'phase' | 'team' | 'priority' | 'assignee' | 'teamAssignees'
    >;
    onBackToCases: () => void;
    onContinue?: () => void;
    // Absent when nothing follows this case in the list it was opened from.
    nextCase?: NextCaseLink;
}

export function CompletionCard({
    completedCase: c,
    completedAction,
    nextStatus,
    onBackToCases,
    onContinue,
    nextCase
}: CompletionCardProps) {
    const catalog = useStatusCatalog();
    const practiceLabel = practiceDoctorLabel(c);
    // Prefer the refetched status: exit-hold resumes to resume_from_status, so the
    // edge's declared target is only a fallback for when the refetch failed.
    const newStatusLabel = catalog.display(nextStatus?.statusId ?? completedAction.to);
    const newPhase = nextStatus?.phase ?? c.phase;
    const newTeam = nextStatus?.team ?? c.team;
    const initialTeamAssignees = nextStatus ? nextStatus.teamAssignees : c.teamAssignees;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-6 py-7 flex flex-col gap-3 max-w-[660px] mx-auto w-full">
                    {/* ── Success hero ── */}
                    <div className="bg-[#CBEDE0] border border-[#B6E4D2] rounded-[14px] px-7 py-8 text-center relative overflow-hidden">
                        {/* Radial highlight — kept as inline style; no Tailwind equivalent for complex radial gradients */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background:
                                    'radial-gradient(120% 90% at 50% -20%, rgba(255,255,255,.55), rgba(255,255,255,0) 60%)'
                            }}
                        />

                        {/* Avatar */}
                        <div className="relative w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center font-bold text-[22px] select-none ring-4 ring-white shadow-md bg-yellow-400 text-[#4A3A02]">
                            {initials(c.name)}
                        </div>

                        {/* Patient name + practice */}
                        <div className="relative text-[20px] font-bold text-gray-900 leading-snug">
                            {c.name}
                        </div>
                        {practiceLabel && (
                            <div className="relative mt-0.5 text-[13px] text-[#3D6957]">
                                {practiceLabel}
                            </div>
                        )}

                        {/* Done chip */}
                        <div className="relative inline-flex items-center gap-1.5 bg-white mt-4 px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold text-[#1E8047] border border-[#D8EEE2]">
                            <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0 text-[#2FA85A]" />
                            {completedAction.label}
                        </div>
                    </div>

                    {/* ── Status transition card ── */}
                    <div className="bg-white rounded-[14px] border border-gray-200 flex items-stretch gap-3.5 px-5 py-[18px]">
                        {/* From */}
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.08em] uppercase mb-2 text-[#AAB0B9]">
                                Completed
                            </div>
                            <div className="flex items-center gap-2 rounded-[10px] px-3 py-2.5 bg-[#F7F8FA] border border-[#E7E9ED] opacity-[0.92]">
                                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-[#AAB0B9]" />
                                <span className="flex-1 min-w-0 text-[13.5px] font-semibold truncate text-[#565D67]">
                                    {catalog.display(c.statusId)}
                                </span>
                                <TeamPill team={c.team} size="xs" />
                            </div>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center self-end pb-[10px] text-[#AAB0B9]">
                            <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </div>

                        {/* To */}
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.08em] uppercase mb-2 text-[#AAB0B9]">
                                New status
                            </div>
                            <div className="flex items-center gap-2 rounded-[10px] px-3 py-2.5 bg-[#E9F6EE] border border-[#CDE9D7]">
                                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-[#2FA85A]" />
                                <span className="flex-1 min-w-0 text-[13.5px] font-semibold text-gray-800 truncate">
                                    {newStatusLabel}
                                </span>
                                <TeamPill team={newTeam} size="xs" />
                            </div>
                        </div>
                    </div>

                    {/* ── Facts card ── */}
                    <div className="bg-white rounded-[14px] border border-gray-200 grid grid-cols-3 p-1">
                        <div className="px-4 py-3.5 border-r border-gray-100">
                            <div className="text-[10px] font-bold tracking-[0.08em] uppercase mb-1.5 text-[#AAB0B9]">
                                Phase
                            </div>
                            <div className="text-[14px] font-semibold text-gray-800">
                                {newPhase}
                            </div>
                        </div>
                        <div className="px-4 py-3.5 border-r border-gray-100">
                            <div className="text-[10px] font-bold tracking-[0.08em] uppercase mb-1.5 text-[#AAB0B9]">
                                Now with
                            </div>
                            <div className="flex items-center gap-2">
                                <TeamPill team={newTeam} size="xs" />
                                <span className="text-[13px] font-medium text-[#565D67]">team</span>
                            </div>
                        </div>
                        <div className="px-4 py-3.5">
                            <div className="text-[10px] font-bold tracking-[0.08em] uppercase mb-1.5 text-[#AAB0B9]">
                                Priority
                            </div>
                            {/* Editable: reprioritizing right after completing a step is common
                  (e.g. bump a case that now waits on the manufacturer). */}
                            <PrioritySelector
                                patientId={c.id}
                                priority={nextStatus?.priority ?? c.priority}
                            />
                        </div>
                        {/* Editable: the case just moved to a new team - handing it to
                the right person is a natural part of finishing the task. */}
                        <CompletionCardAssignees
                            workCase={c}
                            initialTeamAssignees={initialTeamAssignees}
                            newTeam={newTeam}
                            initialAssignee={nextStatus ? nextStatus.assignee : c.assignee}
                        />
                    </div>

                    {/* ── Next steps note ── */}
                    <div className="flex gap-2.5 rounded-[10px] px-4 py-3.5 text-[12.5px] leading-relaxed bg-[#F7F8FA] border border-[#EFF1F4] text-[#565D67]">
                        <InformationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#34577F]" />
                        <span>
                            The case now sits in{' '}
                            <span className="font-semibold text-gray-800">{newStatusLabel}</span>{' '}
                            with the <span className="font-semibold text-gray-800">{newTeam}</span>{' '}
                            team.{' '}
                            {onContinue
                                ? 'Continue to the next action, or head back to your queue.'
                                : 'Head back to your queue to see it.'}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Footer ── */}
            <div className="border-t border-gray-200 bg-white flex-shrink-0 flex items-center justify-end gap-2.5 px-6 py-3">
                {nextCase && <NextCaseButton link={nextCase} />}
                <div className="ml-auto flex items-center gap-2.5">
                    <button
                        onClick={onBackToCases}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] border border-gray-200 bg-white text-[13px] font-semibold text-[#565D67] hover:bg-gray-50 transition-colors"
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        >
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                        Back to cases
                    </button>
                    {onContinue && (
                        <button
                            onClick={onContinue}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[13px] font-semibold text-white bg-[#16191F] hover:bg-[#23272F] transition-colors"
                        >
                            {newStatusLabel}
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
